/* ==========================================================================
   <flux-collectie> — filters en sortering op de collectiepagina.

   Zonder JavaScript is het een gewoon GET-formulier (sections/flux-collectie).
   Met JavaScript:
   - Elke wijziging (vinkje, merk, sortering) past direct toe. Prijsvelden
     wachten tot je even stopt met typen.
   - Alleen deze sectie wordt opnieuw opgehaald (?section_id=…), en alleen de
     delen met data-vervang worden vervangen. Het formulier zelf blijft staan,
     dus de lade blijft open en de focus blijft waar hij was.
   - De URL loopt mee (history), zodat terug/vooruit en delen werken.
   - Links met data-filterlink (actieve filters, paginering, "Alle merken")
     laden op dezelfde manier. Bij paginering gaat het scherm naar boven.
   - Na elke wijziging leest [data-status] het nieuwe aantal voor.
   - Sorteren op beoordeling bestaat in Shopify niet: de kaarten van deze
     pagina worden op data-beoordeling gesorteerd (reviews.rating).
   - Onder lg opent de knop Filters het paneel als modale lade: role=dialog,
     de rest van de pagina inert, Escape sluit.
   ========================================================================== */

(() => {
  const BEOORDELING = 'beoordeling';
  const GROOT = '(min-width: 1024px)';

  class FluxCollectie extends HTMLElement {
    connectedCallback() {
      this.sectie = this.dataset.sectie;
      this.form = this.querySelector('[data-filterformulier]');
      this.paneel = this.querySelector('[data-filterpaneel]');
      this.openKnop = this.querySelector('[data-filters-openen]');
      if (!this.form) return;

      this.opruimen = new AbortController();
      const { signal } = this.opruimen;

      this.form.addEventListener('change', (e) => {
        if (e.target.matches('[data-prijs]')) return;
        this.#pasToe();
      }, { signal });
      this.form.addEventListener('input', (e) => {
        if (!e.target.matches('[data-prijs]')) return;
        clearTimeout(this.wacht);
        this.wacht = setTimeout(() => this.#pasToe(), 700);
      }, { signal });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.#pasToe();
      }, { signal });

      this.addEventListener('click', (e) => {
        const link = e.target.closest('a[data-filterlink]');
        if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
          e.preventDefault();
          this.#laad(link.href, { naarBoven: link.hasAttribute('data-paginering') });
          return;
        }
        if (e.target.closest('[data-filters-openen]')) this.#lade(true);
        else if (e.target.closest('[data-filters-sluiten]')) this.#lade(false);
      }, { signal });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.#ladeOpen()) this.#lade(false);
      }, { signal });

      // Alleen onze eigen geschiedenis: een #anker (skiplink) laat hij met rust.
      window.addEventListener('popstate', (e) => {
        if (e.state?.fluxCollectie) this.#laad(location.href, { geschiedenis: false });
      }, { signal });
      history.replaceState({ ...history.state, fluxCollectie: true }, '');

      matchMedia(GROOT).addEventListener('change', (e) => {
        if (e.matches && this.#ladeOpen()) this.#lade(false);
      }, { signal });

      this.#sorteerderKoppelen(signal);
      this.#sorteerOpBeoordeling();
    }

    /* ---------------------------------------------------- sorteerlijst

       Select-only combobox (WAI-ARIA APG): de focus blijft op de knop, de
       actieve optie loopt via aria-activedescendant. De native select draagt
       de waarde; een keuze zet die en stuurt 'change', zodat het formulier
       hem net als een filter toepast. */

    #sorteerderKoppelen(signal) {
      this.sKnop = this.querySelector('[data-sorteer-knop]');
      this.sLijst = this.querySelector('[data-sorteer-lijst]');
      this.sSelect = this.querySelector('[data-sorteer]');
      if (!this.sKnop || !this.sLijst || !this.sSelect) return;
      this.#toonSortering();

      this.sKnop.addEventListener('click', () => this.#lijst(this.sLijst.hidden), { signal });
      this.sKnop.addEventListener('keydown', (e) => this.#sorteerToets(e), { signal });
      this.sKnop.addEventListener('blur', () => this.#lijst(false), { signal });
      // mousedown i.p.v. click: vóór de blur van de knop, anders sluit de
      // lijst voordat de keuze binnenkomt.
      this.sLijst.addEventListener('mousedown', (e) => {
        const optie = e.target.closest('[role="option"]');
        e.preventDefault();
        if (optie) this.#kies(optie);
      }, { signal });
      this.sLijst.addEventListener('mousemove', (e) => {
        const optie = e.target.closest('[role="option"]');
        if (optie) this.#actief(optie);
      }, { signal });
    }

    #opties() {
      return [...this.sLijst.querySelectorAll('[role="option"]')];
    }

    #toonSortering() {
      if (!this.sKnop) return;
      const waarde = this.sSelect.value;
      for (const o of this.#opties()) o.setAttribute('aria-selected', String(o.dataset.waarde === waarde));
      this.sKnop.querySelector('[data-sorteer-tekst]').textContent =
        this.sSelect.selectedOptions[0]?.textContent.trim() || '';
    }

    #lijst(open) {
      if (!this.sLijst || this.sLijst.hidden === !open) return;
      this.sLijst.hidden = !open;
      this.sKnop.setAttribute('aria-expanded', String(open));
      if (open) this.#actief(this.#opties().find((o) => o.dataset.waarde === this.sSelect.value) || this.#opties()[0]);
      else this.sKnop.removeAttribute('aria-activedescendant');
    }

    #actief(optie) {
      for (const o of this.#opties()) o.toggleAttribute('data-actief', o === optie);
      if (optie) this.sKnop.setAttribute('aria-activedescendant', optie.id);
    }

    #kies(optie) {
      this.#lijst(false);
      if (this.sSelect.value === optie.dataset.waarde) return;
      this.sSelect.value = optie.dataset.waarde;
      this.#toonSortering();
      this.sSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    #sorteerToets(e) {
      const opties = this.#opties();
      const open = !this.sLijst.hidden;
      const nu = opties.findIndex((o) => o.hasAttribute('data-actief'));
      const naar = (i) => this.#actief(opties[Math.max(0, Math.min(opties.length - 1, i))]);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!open) this.#lijst(true);
          else naar(nu + 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!open) this.#lijst(true);
          else naar(nu - 1);
          break;
        case 'Home':
          if (open) { e.preventDefault(); naar(0); }
          break;
        case 'End':
          if (open) { e.preventDefault(); naar(opties.length - 1); }
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (open && nu >= 0) this.#kies(opties[nu]);
          else this.#lijst(true);
          break;
        case 'Escape':
          if (open) { e.preventDefault(); e.stopPropagation(); this.#lijst(false); }
          break;
        case 'Tab':
          if (open) this.#lijst(false);
          break;
        default:
          // Typen springt naar de eerste optie die met die letter begint.
          if (open && e.key.length === 1) {
            const doel = opties.find((o) => o.textContent.trim().toLowerCase().startsWith(e.key.toLowerCase()));
            if (doel) this.#actief(doel);
          }
      }
    }

    disconnectedCallback() {
      this.opruimen?.abort();
      this.verzoek?.abort();
      if (this.#ladeOpen()) this.#lade(false);
    }

    /* URL uit het formulier: lege velden (prijs) laten we weg. Parameters die
       niet van het formulier zijn, blijven staan: /collections/types?q=Laadpaal
       en /collections/vendors?q=Alfen bestaan alleen dankzij die q. */
    #url() {
      const params = new URLSearchParams();
      for (const [naam, waarde] of new URLSearchParams(location.search)) {
        if (!naam.startsWith('filter.') && !['sort_by', 'page', 'section_id'].includes(naam)) params.append(naam, waarde);
      }
      for (const [naam, waarde] of new FormData(this.form)) {
        if (String(waarde).trim() !== '') params.append(naam, waarde);
      }
      const query = params.toString();
      return `${location.pathname}${query ? `?${query}` : ''}`;
    }

    #pasToe() {
      this.#laad(this.#url());
    }

    async #laad(adres, { geschiedenis = true, naarBoven = false } = {}) {
      const doel = new URL(adres, location.href);
      const ophalen = new URL(doel);
      ophalen.searchParams.set('section_id', this.sectie);
      // Shopify kent 'beoordeling' niet: haal de standaardvolgorde op en
      // sorteer daarna hier.
      if (ophalen.searchParams.get('sort_by') === BEOORDELING) ophalen.searchParams.delete('sort_by');

      this.verzoek?.abort();
      this.verzoek = new AbortController();
      this.setAttribute('aria-busy', 'true');

      try {
        const antwoord = await fetch(ophalen, { signal: this.verzoek.signal });
        if (!antwoord.ok) throw new Error(antwoord.status);
        const doc = new DOMParser().parseFromString(await antwoord.text(), 'text/html');
        if (doel.searchParams.get('sort_by') === BEOORDELING) this.#houdSortering(doc);
        this.#vervang(doc, { naarBoven });
        if (geschiedenis) history.pushState({ fluxCollectie: true }, '', doel);
        this.#zetSortering(doel);
        this.#sorteerOpBeoordeling();
        this.#meld();
      } catch (fout) {
        if (fout.name === 'AbortError') return;
        location.href = doel; // terugvallen op een gewone paginalading
        return;
      }
      this.removeAttribute('aria-busy');
    }

    /* Vervang de data-vervang-delen. Open/dicht van de filtergroepen en de
       focus blijven behouden; bestaat het gefocuste element niet meer (een
       verwijderde filterchip), dan gaat de focus naar het resultatenaantal. */
    #vervang(doc, { naarBoven }) {
      const groepen = [...this.querySelectorAll('details[data-groep]')];
      const staat = new Map(groepen.map((d) => [d.dataset.groep, d.open]));
      const meerOpen = new Set(
        groepen.filter((d) => d.querySelector('details[data-meer]')?.open).map((d) => d.dataset.groep)
      );
      const actief = document.activeElement;
      const hadFocus = this.contains(actief);
      const focusId = hadFocus ? actief.id : null;

      for (const oud of this.querySelectorAll('[data-vervang]')) {
        const nieuw = doc.querySelector(`[data-vervang="${oud.dataset.vervang}"]`);
        if (nieuw) oud.replaceWith(document.importNode(nieuw, true));
        else if (oud.dataset.vervang !== 'merken') oud.replaceChildren();
      }

      for (const d of this.querySelectorAll('details[data-groep]')) {
        if (staat.has(d.dataset.groep)) d.open = staat.get(d.dataset.groep);
        if (meerOpen.has(d.dataset.groep)) d.querySelector('details[data-meer]')?.setAttribute('open', '');
      }

      const aantal = this.querySelector('[data-vervang="aantal"]');
      if (naarBoven) {
        const snel = matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ block: 'start', behavior: snel ? 'auto' : 'smooth' });
        aantal?.focus({ preventScroll: true });
        return;
      }
      if (!hadFocus) return;
      const terug = focusId && document.getElementById(focusId);
      if (terug && this.contains(terug)) terug.focus({ preventScroll: true });
      else if (this.#ladeOpen()) this.paneel.querySelector('[data-filters-sluiten]')?.focus();
      else aantal?.focus({ preventScroll: true });
    }

    /* Voorleesregel: eerst leeg, dan in het volgende frame de tekst, zodat
       schermlezers ook een gelijk aantal opnieuw voorlezen. */
    #meld() {
      const status = this.querySelector('[data-status]');
      const tekst = this.querySelector('[data-vervang="aantal"]')?.textContent.trim();
      if (!status || !tekst) return;
      status.textContent = '';
      requestAnimationFrame(() => {
        status.textContent = tekst;
      });
    }

    /* De sorteerkeuze staat buiten data-vervang: zet hem vanuit de URL, anders
       toont hij na terug/vooruit de oude keuze en stuurt hij die mee. */
    #zetSortering(url) {
      const kiezer = this.querySelector('[data-sorteer]');
      if (!kiezer) return;
      const waarde = url.searchParams.get('sort_by');
      const bestaat = [...kiezer.options].some((o) => o.value === waarde);
      kiezer.value = bestaat ? waarde : kiezer.options[0]?.value;
      this.#toonSortering();
    }

    /* De links uit Shopify (actieve filters, paginering) kennen alleen de
       sortering die opgehaald is, en 'beoordeling' halen we zonder sortering
       op. Zet hem er weer in, anders valt hij weg bij de volgende klik. */
    #houdSortering(root) {
      for (const a of root.querySelectorAll('a[data-filterlink]')) {
        const url = new URL(a.getAttribute('href'), location.origin);
        url.searchParams.set('sort_by', BEOORDELING);
        a.setAttribute('href', url.pathname + url.search);
      }
    }

    #sorteerOpBeoordeling() {
      const kiezer = this.querySelector('[data-sorteer]');
      const gekozen = new URLSearchParams(location.search).get('sort_by') === BEOORDELING;
      if (kiezer && gekozen) {
        kiezer.value = BEOORDELING;
        this.#houdSortering(this);
      }
      this.#toonSortering();
      const raster = this.querySelector('[data-raster]');
      if (!raster) return;

      const items = [...raster.children];
      const cijfer = (li, naam) => parseFloat(li.querySelector('[data-slot="card"]')?.dataset[naam]) || 0;
      items.sort((a, b) =>
        gekozen
          ? cijfer(b, 'beoordeling') - cijfer(a, 'beoordeling') ||
            cijfer(b, 'beoordelingen') - cijfer(a, 'beoordelingen') ||
            a.dataset.positie - b.dataset.positie
          : a.dataset.positie - b.dataset.positie
      );
      raster.append(...items);
    }

    #ladeOpen() {
      return Boolean(this.paneel?.hasAttribute('data-open'));
    }

    /* Modale lade onder lg. Alles buiten het paneel wordt inert: op elk
       niveau van het paneel tot <body> de broers en zussen. Zo blijft Tab in
       de lade en lezen schermlezers de pagina erachter niet voor. */
    #lade(open) {
      if (!this.paneel) return;
      this.paneel.toggleAttribute('data-open', open);
      this.openKnop?.setAttribute('aria-expanded', String(open));
      document.documentElement.style.overflow = open ? 'hidden' : '';

      if (open) {
        const titel = this.paneel.querySelector('[id^="Filterpaneel-titel"]');
        this.paneel.setAttribute('role', 'dialog');
        this.paneel.setAttribute('aria-modal', 'true');
        if (titel) this.paneel.setAttribute('aria-labelledby', titel.id);
        this.inertGezet = [];
        for (let el = this.paneel; el && el !== document.body; el = el.parentElement) {
          for (const broer of el.parentElement?.children || []) {
            if (broer === el || broer.inert || ['SCRIPT', 'STYLE', 'LINK'].includes(broer.tagName)) continue;
            broer.inert = true;
            this.inertGezet.push(broer);
          }
        }
        this.paneel.querySelector('[data-filters-sluiten]')?.focus();
        return;
      }

      this.paneel.removeAttribute('role');
      this.paneel.removeAttribute('aria-modal');
      this.paneel.removeAttribute('aria-labelledby');
      for (const el of this.inertGezet || []) el.inert = false;
      this.inertGezet = [];
      // Alleen terug naar de knop als die zichtbaar is (onder lg).
      if (this.openKnop?.offsetParent) this.openKnop.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('flux-collectie')) customElements.define('flux-collectie', FluxCollectie);
})();
